import { computed, ref } from 'vue';
import { listTermReasons, saveTermReason } from '@/api/settingsApi';
import { useSettingsEntityList } from './useSettingsEntityList';

const DEFAULT_CATEGORIES = [
  'Personal reason',
  'Truck issues',
  'Documents',
  'Not meeting company standards',
  'Medical leave',
  'Other'
];

export function useTermReasons() {
  const { afterMutation } = useSettingsEntityList();

  const loading = ref(false);
  const saving = ref(false);
  const loadedOnce = ref(false);
  const error = ref(null);
  const reasons = ref([]);
  const categories = ref([...DEFAULT_CATEGORIES]);
  const searchQuery = ref('');

  const filteredReasons = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return reasons.value;
    return reasons.value.filter((r) => {
      const hay = [r.label, r.category, r.reasonId]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  });

  const countLabel = computed(() => {
    const n = filteredReasons.value.length;
    const total = reasons.value.length;
    if (searchQuery.value.trim() && n !== total) {
      return `${n} of ${total} reasons`;
    }
    return `${total} reason${total === 1 ? '' : 's'}`;
  });

  async function loadReasons() {
    loading.value = true;
    error.value = null;
    try {
      const data = await listTermReasons({ includeInactive: true });
      reasons.value = Array.isArray(data?.reasons) ? data.reasons : [];
      if (Array.isArray(data?.categories) && data.categories.length) {
        categories.value = data.categories;
      }
      loadedOnce.value = true;
    } catch (e) {
      error.value = e?.message || 'Failed to load termination reasons';
    } finally {
      loading.value = false;
    }
  }

  async function saveReason(payload) {
    saving.value = true;
    error.value = null;
    try {
      const data = await saveTermReason(payload);
      await afterMutation(async () => {
        if (Array.isArray(data?.reasons)) {
          reasons.value = data.reasons;
        } else {
          await loadReasons();
        }
        if (Array.isArray(data?.categories) && data.categories.length) {
          categories.value = data.categories;
        }
      });
      return data?.reason || null;
    } catch (e) {
      error.value = e?.message || 'Failed to save termination reason';
      throw e;
    } finally {
      saving.value = false;
    }
  }

  return {
    loading,
    saving,
    loadedOnce,
    error,
    reasons,
    categories,
    searchQuery,
    filteredReasons,
    countLabel,
    loadReasons,
    saveReason
  };
}
